import "./style.sass"
import workersUrl from "@opendaw/studio-core/workers-main.js?worker&url"
import workletsUrl from "@opendaw/studio-core/processors.js?url"
import wasmProcessorUrl from "@opendaw/studio-core-wasm/wasm-processor.js?url"
import wasmOfflineWorkerUrl from "@opendaw/studio-core-wasm/wasm-offline-worker.js?worker&url"
import {int, MutableObservableOption} from "@opendaw/lib-std"
import {Promises} from "@opendaw/lib-runtime"
import {createElement, replaceChildren} from "@opendaw/lib-jsx"
import {AnimationFrame} from "@opendaw/lib-dom"
import {compose, setup, Track} from "@/boot"
import smileyUrl from "@/assets/acid-smiley.svg?url"
import {Vhs} from "@/visuals/Vhs"
import {Isolation} from "@/isolation"

const tape: HTMLCanvasElement = <canvas className="tape"/>
const hint = <div className="hint">acid</div>
const smiley: HTMLImageElement = <img className="smiley" src={smileyUrl} alt="acid smiley" draggable={false}/>
const prev: HTMLButtonElement = <button>prev</button>
const next: HTMLButtonElement = <button>next</button>
const open: HTMLButtonElement = <button>open in opendaw studio</button>
const bar = <div className="bar hidden">
    <div className="row">{prev}{next}</div>
    {open}</div>
const face = <div className="face">{smiley}
    <div className="below">{hint}{bar}</div>
</div>
const stage = <div className="stage">{face}</div>
const pause: HTMLButtonElement = <button>pause</button>
const transport = <div className="bar transport hidden">{pause}</div>
const banner = <a className="banner" href="https://github.com/andremichelle/acid"
                 target="_blank" rel="noopener noreferrer" aria-label="acid source code on github">source code</a>

replaceChildren(document.body, tape, stage, transport, banner)

// The tape draws the face itself, so the DOM one only keeps the layout. Without WebGL it stays visible.
const vhs = Vhs.create(tape, smiley, face)
vhs.ifSome(() => smiley.classList.add("ghost"))

// The slot under the face shows either a message or the buttons, never both, so both land at the same offset.
const progress = (message: string): void => {
    hint.textContent = message
    hint.classList.remove("hidden")
    bar.classList.add("hidden")
}
const idle = (): void => {
    hint.textContent = ""
    hint.classList.add("hidden")
    bar.classList.remove("hidden")
}
const newSeed = (): int => Math.floor(Math.random() * 0x7FFFFFFF)

// Every seed ever played, in order. "prev" walks back, "next" walks forward and only draws a new seed at the end.
const seeds: int[] = [newSeed()]
let cursor: int = 0
const busy = (value: boolean): void => {
    prev.disabled = value || cursor === 0
    next.disabled = value
    open.disabled = value
    stage.classList.toggle("busy", value)
}

// The studio reads the raw project (no samples, those resolve by uuid) from the fragment, so it never hits a server.
const StudioOpenUrl = "https://opendaw.studio/open-project"
const studioUrl = async ({project, spec}: Track): Promise<string> => {
    const stream = new Blob([project.toArrayBuffer() as ArrayBuffer]).stream().pipeThrough(new CompressionStream("deflate-raw"))
    const bytes = new Uint8Array(await new Response(stream).arrayBuffer())
    const base64url = btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(""))
        .replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "")
    return `${StudioOpenUrl}?name=${encodeURIComponent(`Acid ${spec.seed}`)}#${base64url}`
}

const run = async (): Promise<void> => {
    AnimationFrame.start(window)
    const env = await setup({workersUrl, workletsUrl, wasmProcessorUrl, wasmOfflineWorkerUrl}, progress)
    const current = new MutableObservableOption<Track>()
    // Encoded ahead of time, so the click resolves within the user activation and popup blockers stay quiet.
    let openUrl: Promise<string> = Promise.resolve(StudioOpenUrl)
    const replace = (track: Track): void => {
        current.clear(previous => previous.project.terminate())
        current.wrap(track)
        openUrl = studioUrl(track)
        openUrl.then(url => console.debug(`studio url ${url.length} chars`))
        vhs.ifSome(vhs => track.project.own(vhs.listen(track.project,
            track.drums.audioUnitBox.address, track.bass.audioUnitBox.address)))
        if (import.meta.env.DEV) {Object.assign(window, {acid: {...env, ...track}})}
    }
    replace(await compose(env, seeds[cursor], progress))
    busy(false)
    const jump = async (index: int): Promise<void> => {
        if (index < 0) {return}
        if (index === seeds.length) {seeds.push(newSeed())}
        busy(true)
        const {status, value: track, error} = await Promises.tryCatch(compose(env, seeds[index], progress))
        if (status === "rejected") {
            busy(false)
            console.error(error)
            progress(String(error))
            return
        }
        cursor = index
        busy(false)
        replace(track)
        track.arranger.start()
        idle()
    }
    // Deliberately not studio-core's AudioContexts.resume: that one returns false WITHOUT calling resume
    // whenever the state is not exactly "suspended", which dead-ends on Safari's "interrupted" and on any
    // state Chrome reports that we did not anticipate. Ask from every state but "running", then confirm.
    // Returns null when it plays, otherwise the reason, which the caller shows so a report can name it.
    const resumeAudio = async (): Promise<string | null> => {
        const context = env.audioContext
        // Read through this: the state does change across the await, but the narrowing from the early
        // return below does not, and the compiler would call the second comparison impossible.
        const state = (): string => context.state
        if (state() === "running") {return null}
        // Scheduling something from inside the gesture is what actually hands the output over on iOS.
        const source = context.createBufferSource()
        source.buffer = context.createBuffer(1, 1, context.sampleRate)
        source.connect(context.destination)
        source.start()
        const {status, error} = await Promises.tryCatch(context.resume())
        if (status === "rejected") {
            console.warn("AudioContext.resume rejected", error, {state: state(), sampleRate: context.sampleRate})
            return error instanceof Error ? error.name : String(error)
        }
        // A resolved resume is not a running context: a browser that blocks audio resolves it and stays put.
        if (state() === "running") {return null}
        console.warn(`AudioContext.resume resolved but state is ${state()}`)
        return state()
    }
    // Suspending the context freezes engine, clip clock and meters in place, so resume picks up exactly there.
    // One place owns the label, so the button and the open-in-studio handover never disagree.
    const setPlaying = async (playing: boolean): Promise<string | null> => {
        if (playing) {
            const failure = await resumeAudio()
            if (failure !== null) {return failure}
        } else {
            await env.audioContext.suspend()
        }
        pause.textContent = playing ? "pause" : "resume"
        return null
    }
    pause.onclick = () => setPlaying(env.audioContext.state !== "running")
    prev.onclick = () => jump(cursor - 1)
    next.onclick = () => jump(cursor + 1)
    open.onclick = async () => {
        // The studio takes over playback of this track, so hand the sound over with it.
        window.open(await openUrl, "_blank")
        await setPlaying(false)
    }
    // No browser starts audio without a gesture, and iOS Safari only counts a few event types as one: a
    // `pointerdown` is not enough there, a `click` is. Capture, so a click on a button still arms it, and it
    // stays subscribed until a resume actually succeeds, keeping a refusal retryable (as the studio boots).
    const start = async () => {
        const failure = await setPlaying(true)
        if (failure !== null) {
            progress(`could not start audio (${failure}), tap again`)
            return
        }
        window.removeEventListener("click", start, {capture: true})
        current.unwrap().arranger.start()
        idle()
        transport.classList.remove("hidden")
    }
    progress("click to play")
    window.addEventListener("click", start, {capture: true})
}

Isolation.recover().then(isolated => {
    if (!isolated) {
        progress("reload to enable audio")
        return
    }
    run().catch(reason => {
        console.error(reason)
        progress(String(reason))
    })
})
